/**
 * Agregados de D-Talk para la vista de People (requerimiento del 25/08).
 *
 * Vive en shared/ a propósito: el backend los devuelve calculados sobre TODA
 * la base, y el front los recalcula sobre las filas filtradas (área, país,
 * líder, impact level…) sin pegarle de nuevo a la API. Una sola definición de
 * qué es "convocada", "completada" y del denominador.
 */

export type DTalkCategoriaKey = 'promocion' | 'movimiento_horizontal' | 'sin_claridad';

export const DTALK_CATEGORIA_LABEL: Record<DTalkCategoriaKey, string> = {
  promocion: 'Promoción',
  movimiento_horizontal: 'Movimiento horizontal',
  sin_claridad: 'Sin claridad de próximo paso',
};

/** Lo mínimo que necesita el cálculo (lo cumple la fila de listAllForPeople). */
export interface DTalkStatsRow {
  employeeEmail: string;
  employeeName: string;
  employeeLastName: string;
  employeeArea: string | null;
  eligibilityStatus: string;
  processStatus: string;
  categoriaAspiracion?: string | null;
  categoriaAspiracionSecundaria?: string | null;
  aspiracionesSecundarias?: string[];
  alerta?: boolean;
  motivosAlerta?: string[];
  licenseExcludedBy?: string | null;
  licenseExcludedAt?: string | null;
}

export interface DTalkRanked {
  key: string;
  count: number;
  pct: number;
}

export interface DTalkPeopleStats {
  totalPersonas: number;
  convocadas: number;
  enLicencia: number;
  sinAntiguedad: number;
  inactivas: number;
  estado: { completadas: number; enCurso: number; noIniciadas: number };
  pctCompletadas: number;
  categorias: DTalkRanked[];
  sinCategoria: number;
  secundarias: { personas: number; porCategoria: DTalkRanked[] };
  alerta: { personas: number; motivos: DTalkRanked[] };
  licencias: Array<{
    employeeEmail: string;
    employeeName: string;
    employeeArea: string | null;
    licenseExcludedBy: string | null;
    licenseExcludedAt: string | null;
  }>;
}

/** KPI de 3 cajas (completadas / en curso / no iniciadas): acá 'pendiente de validación' cuenta como en curso. Para chips y filtros usar dtalkDisplayStatus (4 estados). */
export const EN_CURSO = ['in_progress', 'paused', 'pending_validation', 'reopened'];

/**
 * Los 4 estados que se MUESTRAN (Flor Menéndez, 07/10/2026: "deberían ser 4").
 * El agente sigue guardando sus estados internos (pausada, reabierta…); acá se
 * agrupan para chips, filtros, barras y CSV. Una persona no elegible (licencia,
 * onboarding, inactiva) no tiene estado de proceso: se muestra su elegibilidad.
 */
export const DTALK_DISPLAY_STATUSES = ['not_started', 'in_progress', 'pending_validation', 'completed'] as const;
export type DTalkDisplayStatus = (typeof DTALK_DISPLAY_STATUSES)[number];

/** Estado interno → uno de los 4 visibles; `null` si no es elegible. */
export function dtalkDisplayStatus(processStatus: string | null | undefined): DTalkDisplayStatus | null {
  switch (processStatus) {
    case 'completed': return 'completed';
    case 'pending_validation': return 'pending_validation';
    case 'in_progress':
    case 'paused':
    case 'reopened':
    case 'closed_window': return 'in_progress'; // la ventana cerró con la charla empezada (nadie lo escribe hoy)
    case 'not_eligible': return null;
    default: return 'not_started';
  }
}

/** Lo mínimo del registro para saber hasta dónde llegó la conversación. */
export interface DTalkProgressEvidence {
  processStatus?: string | null;
  conversationHistory?: ReadonlyArray<{ role: string }> | null;
  snapshots?: ReadonlyArray<{ type: string }> | null;
  conclusionParaValidar?: string | null;
  conclusionValidada?: string | null;
  validatedAt?: string | null;
}

/**
 * Estado según lo que REALMENTE pasó en la conversación, sin mirar el estado
 * guardado. Lo usa la reincorporación de una licencia (no hay estado previo
 * guardado en los casos viejos) y el script de reparación: marcar licencia
 * pisaba el estado y desmarcarla dejaba "Sin iniciar" a gente con la charla
 * hecha (Emanuel Parra, Mariano Hernández, Angie Flórez, Ignacio Ianero,
 * 07/10/2026).
 */
export function deriveDTalkProcessStatus(r: DTalkProgressEvidence): 'not_started' | 'in_progress' | 'pending_validation' | 'completed' {
  const types = (r.snapshots ?? []).map((x) => x.type);
  const lastValid = types.lastIndexOf('validada');
  const lastReopen = types.lastIndexOf('reapertura');
  // Validada y sin reapertura posterior. Sin snapshots, alcanza con la validación guardada.
  if (lastValid > lastReopen || (lastValid < 0 && lastReopen < 0 && (r.validatedAt || r.conclusionValidada))) return 'completed';
  if (r.conclusionParaValidar) return 'pending_validation';
  if ((r.conversationHistory ?? []).some((m) => m.role === 'user')) return 'in_progress';
  return 'not_started';
}

/**
 * Estado a mostrar/contar: el guardado, salvo que diga "Sin iniciar" con una
 * conversación hecha (registros que pisó el botón de licencia antes del
 * 07/10/2026). En ese caso manda la evidencia.
 */
export function effectiveDTalkStatus(r: DTalkProgressEvidence): string {
  const stored = r.processStatus ?? 'not_started';
  if (stored !== 'not_started') return stored;
  return deriveDTalkProcessStatus(r);
}

const pct = (n: number, total: number) => (total > 0 ? Math.round((n / total) * 1000) / 10 : 0);

/**
 * Denominador = personas CONVOCADAS: elegibles, sin las que están en licencia
 * ni las que aún no cumplen 3 meses. Es lo que pidió People para que el % de
 * avance no quede distorsionado.
 */
export function buildDTalkPeopleStats(rows: DTalkStatsRow[]): DTalkPeopleStats {
  const convocadas = rows.filter((r) => r.eligibilityStatus === 'eligible');
  const enLicencia = rows.filter((r) => r.eligibilityStatus === 'license');
  const completadas = convocadas.filter((r) => r.processStatus === 'completed');
  const enCurso = convocadas.filter((r) => EN_CURSO.includes(r.processStatus));
  const noIniciadas = convocadas.filter((r) => r.processStatus === 'not_started');

  const countBy = (get: (r: DTalkStatsRow) => string | null | undefined) => {
    const m = new Map<string, number>();
    for (const r of convocadas) {
      const k = get(r);
      if (!k) continue;
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  };
  const asList = (m: Map<string, number>, total: number): DTalkRanked[] =>
    [...m.entries()]
      .map(([key, count]) => ({ key, count, pct: pct(count, total) }))
      .sort((a, b) => b.count - a.count);

  const categorias = countBy((r) => r.categoriaAspiracion);
  const conCategoria = [...categorias.values()].reduce((a, b) => a + b, 0);
  const secundarias = countBy((r) => r.categoriaAspiracionSecundaria);
  const totalSecundarias = [...secundarias.values()].reduce((a, b) => a + b, 0);

  const conAlerta = convocadas.filter((r) => r.alerta);
  const motivos = new Map<string, number>();
  for (const r of conAlerta) {
    for (const m of r.motivosAlerta ?? []) {
      const k = m.trim();
      if (k) motivos.set(k, (motivos.get(k) ?? 0) + 1);
    }
  }

  return {
    totalPersonas: rows.length,
    convocadas: convocadas.length,
    enLicencia: enLicencia.length,
    sinAntiguedad: rows.filter((r) => r.eligibilityStatus === 'onboarding').length,
    inactivas: rows.filter((r) => r.eligibilityStatus === 'inactive').length,
    estado: {
      completadas: completadas.length,
      enCurso: enCurso.length,
      noIniciadas: noIniciadas.length,
    },
    pctCompletadas: pct(completadas.length, convocadas.length),
    categorias: asList(categorias, conCategoria),
    sinCategoria: convocadas.length - conCategoria,
    secundarias: {
      personas: convocadas.filter((r) => (r.aspiracionesSecundarias ?? []).length > 0).length,
      porCategoria: asList(secundarias, totalSecundarias),
    },
    alerta: {
      personas: conAlerta.length,
      motivos: asList(motivos, conAlerta.length).slice(0, 10),
    },
    licencias: enLicencia.map((r) => ({
      employeeEmail: r.employeeEmail,
      employeeName: `${r.employeeName} ${r.employeeLastName}`.trim(),
      employeeArea: r.employeeArea,
      licenseExcludedBy: r.licenseExcludedBy ?? null,
      licenseExcludedAt: r.licenseExcludedAt ?? null,
    })),
  };
}

// ─── Avance por área (barras apiladas de la Vista People) ─────────────────────

export interface DTalkAreaProgress {
  area: string; // '—' para filas sin área
  /** N de la barra: personas convocadas (eligibilityStatus === 'eligible') del área */
  convocadas: number;
  completadas: number;
  enCurso: number;
  noIniciadas: number;
  /** Normalizados sobre convocadas — suman ~100 (redondeo) */
  pctCompletadas: number;
  pctEnCurso: number;
  pctNoIniciadas: number;
}

/**
 * Resumen comparativo del avance de TODAS las áreas. Mismo denominador que el
 * resto de las stats (convocadas = eligible). Orden: % completadas desc.
 */
export function buildDTalkAreaProgress(rows: DTalkStatsRow[]): DTalkAreaProgress[] {
  const byArea = new Map<string, DTalkStatsRow[]>();
  for (const r of rows) {
    if (r.eligibilityStatus !== 'eligible') continue;
    const key = (r.employeeArea || '').trim() || '—';
    const list = byArea.get(key) ?? [];
    list.push(r);
    byArea.set(key, list);
  }
  const pctInt = (n: number, total: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
  return [...byArea.entries()]
    .map(([area, list]) => {
      const completadas = list.filter((r) => r.processStatus === 'completed').length;
      const enCurso = list.filter((r) => EN_CURSO.includes(r.processStatus)).length;
      const noIniciadas = list.length - completadas - enCurso;
      return {
        area,
        convocadas: list.length,
        completadas,
        enCurso,
        noIniciadas,
        pctCompletadas: pctInt(completadas, list.length),
        pctEnCurso: pctInt(enCurso, list.length),
        pctNoIniciadas: pctInt(noIniciadas, list.length),
      };
    })
    .sort((a, b) => b.pctCompletadas - a.pctCompletadas || b.convocadas - a.convocadas);
}
